import { cn } from '@listening/utils/react-helpers';
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';

const toggleGroupVariants = cva('inline-flex', {
  variants: {
    variant: {
      default: 'space-x-px rounded bg-muted p-1 text-muted-foreground',
      app: 'gap-0.5 rounded-[var(--v2-button-radius)] bg-[var(--v2-chip-bg)] p-[3px] text-[var(--v2-text-muted)]',
    },
  },
  defaultVariants: { variant: 'default' },
});

const ToggleGroup = ({
  ref,
  className,
  variant,
  ...props
}: React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Root> & {
  ref?: React.RefObject<React.ComponentRef<
    typeof ToggleGroupPrimitive.Root
  > | null>;
} & VariantProps<typeof toggleGroupVariants>) => (
  <ToggleGroupPrimitive.Root
    className={cn(toggleGroupVariants({ variant, className }))}
    ref={ref}
    {...props}
  />
);

ToggleGroup.displayName = ToggleGroupPrimitive.Root.displayName;

const toggleGroupItemVariants = cva(
  'inline-flex items-center justify-center font-medium whitespace-nowrap transition-all focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50 data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-2xs',
  {
    variants: {
      variant: {
        default: 'ring-offset-background',
        app: 'text-[var(--v2-text-muted)] hover:text-[var(--v2-text-primary)] data-[state=on]:bg-[var(--v2-page-bg)] data-[state=on]:text-[var(--v2-text-primary)]',
      },
      size: {
        default: 'rounded-sm px-3 py-1.5 text-sm',
      },
    },
    compoundVariants: [
      {
        variant: 'app',
        className: 'rounded-[calc(var(--v2-button-radius)-4px)]',
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

const ToggleGroupItem = ({
  ref,
  className,
  variant,
  size,
  ...props
}: React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item> &
  VariantProps<typeof toggleGroupItemVariants> & {
    ref?: React.RefObject<React.ComponentRef<
      typeof ToggleGroupPrimitive.Item
    > | null>;
  }) => (
  <ToggleGroupPrimitive.Item
    className={cn(toggleGroupItemVariants({ variant, size, className }))}
    ref={ref}
    {...props}
  />
);

ToggleGroupItem.displayName = ToggleGroupPrimitive.Item.displayName;

export { ToggleGroup, ToggleGroupItem };
